import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Heart } from "lucide-react";
import { NAV_LINKS } from "../lib/content";
import logo from "../assets/logo.jpeg";

const Logo = () => (
  <Link to="/" className="flex items-center gap-3 group" data-testid="brand-logo">
    <img
      src={logo}
      alt="Janma Foundation logo"
      className="h-11 w-11 rounded-full object-cover shadow-soft"
    />
    <span className="leading-none">
      <span className="block font-display font-extrabold text-lg tracking-tight text-navy">
        Janma Foundation
      </span>
      <span className="block text-[11px] font-mono tracking-wider mt-1 text-teal-700">
        HEALING HEARTS · HELPING HANDS
      </span>
    </span>
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const goDonate = () => {
    setOpen(false);
    navigate("/get-involved#donate");
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 bg-white transition-shadow duration-300 border-b ${
        scrolled ? "shadow-soft border-slate-200/70" : "border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[74px]">
          <Logo />

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive ? "text-teal-700 bg-teal-50" : "text-slate-600 hover:text-teal-700 hover:bg-teal-50/60"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={goDonate}
              data-testid="donate-header-button"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-coral px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition-all hover:bg-coral-700 hover:-translate-y-0.5 active:translate-y-0"
            >
              <Heart className="h-4 w-4 fill-white" />
              Donate Now
            </button>
            <button
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-open"
              className="lg:hidden grid place-items-center h-11 w-11 rounded-xl bg-teal-50 text-teal-700"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 bg-navy/50 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.aside
              className="fixed top-0 right-0 bottom-0 w-[86%] max-w-sm bg-white z-50 p-6 flex flex-col"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 260 }}
              data-testid="mobile-drawer"
            >
              <div className="flex items-center justify-between mb-8">
                <Logo />
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                  className="grid place-items-center h-10 w-10 rounded-xl bg-slate-100 text-navy"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex flex-col gap-1">
                {NAV_LINKS.map((l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 * i }}
                  >
                    <NavLink
                      to={l.to}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `block px-4 py-3.5 rounded-xl text-lg font-display font-semibold ${
                          isActive ? "text-teal-700 bg-teal-50" : "text-navy hover:bg-slate-50"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </motion.div>
                ))}
              </nav>
              <button
                onClick={goDonate}
                data-testid="donate-drawer-button"
                className="mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-coral px-6 py-4 text-base font-semibold text-white shadow-soft"
              >
                <Heart className="h-5 w-5 fill-white" /> Donate Now
              </button>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};
